import { useState } from 'react';

interface OfflineDeviceRow {
  idEmployee: string;
  Nama: string;
  Jabatan: string | null;
  schedule_type: string;
  shift_date: string;
  window_start: string;
  window_end: string;
  heart_rate_count: number;
  sleep_motion_count: number;
  blood_oxygen_count: number;
  pressure_count: number;
  is_offline: boolean;
  device_name?: string;
  device_id?: string;
}

interface OfflineDevicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  rows?: OfflineDeviceRow[];
  offlineCount: number;
}

export function OfflineDevicesModal({ isOpen, onClose, rows = [], offlineCount }: OfflineDevicesModalProps) {
  const [sortBy, setSortBy] = useState<'Nama' | 'shift_date'>('shift_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  if (!isOpen) return null;

  // Backend already filters only offline devices, no need to filter again
  const offlineRows = rows || [];

  // Sort devices
  const sortedDevices = [...offlineRows].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'Nama':
        comparison = a.Nama.localeCompare(b.Nama);
        break;
      case 'shift_date':
        comparison = new Date(a.shift_date).getTime() - new Date(b.shift_date).getTime();
        break;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="relative bg-white rounded-lg shadow-xl w-full h-full max-w-full max-h-full flex flex-col">
        {/* Header */}
        <div className="bg-slate-700 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-white">Offline Devices</h2>
            <span className="bg-white bg-opacity-20 text-dark px-3 py-1 rounded-full text-sm font-semibold">
              {offlineCount} device{offlineCount !== 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-gray-500/50 rounded-full p-2 transition-colors"
            title="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-6">
          <div className="overflow-x-auto">
            <table className="w-full min-w-max">
              <thead>
                <tr className="border-b-2 border-gray-300 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    <button
                      onClick={() => handleSort('Nama')}
                      className="flex items-center gap-1 hover:text-gray-900"
                    >
                      Employee Name
                      {sortBy === 'Nama' && (
                        <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    Jabatan
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    Device
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    Shift
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    <button
                      onClick={() => handleSort('shift_date')}
                      className="flex items-center gap-1 hover:text-gray-900 mx-auto"
                    >
                      Shift Date
                      {sortBy === 'shift_date' && (
                        <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    HR Count
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    Sleep Count
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    SpO2 Count
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    Pressure Count
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">
                    Note
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedDevices.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                      No offline devices
                    </td>
                  </tr>
                ) : (
                  sortedDevices.map((device, index) => (
                    <tr 
                      key={`${device.idEmployee}-${device.shift_date}-${index}`}
                      className={`border-b border-gray-200 hover:bg-slate-50 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {device.Nama}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {device.Jabatan || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {device.device_name || device.device_id || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                        <span className={`inline-block px-2 py-1 text-xs rounded font-medium ${
                          device.schedule_type === 'day' ? 'bg-yellow-100 text-yellow-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {device.schedule_type === 'day' ? 'Day Shift' : 'Night Shift'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                        {device.shift_date}
                      </td>
                      <td className="px-4 py-3 text-sm text-center whitespace-nowrap font-semibold text-red-600">
                        {device.heart_rate_count}
                      </td>
                      <td className="px-4 py-3 text-sm text-center whitespace-nowrap font-semibold text-red-600">
                        {device.sleep_motion_count}
                      </td>
                      <td className="px-4 py-3 text-sm text-center whitespace-nowrap font-semibold text-red-600">
                        {device.blood_oxygen_count}
                      </td>
                      <td className="px-4 py-3 text-sm text-center whitespace-nowrap font-semibold text-red-600">
                        {device.pressure_count}
                      </td>
                      <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                        <span className="inline-block px-2 py-1 text-xs rounded font-medium bg-slate-100 text-slate-800">
                          Indikasi Device Offline / Tidak Digunakan
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-lg">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {sortedDevices.length} offline device{sortedDevices.length !== 1 ? 's' : ''}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
